import { useCallback, useContext, useEffect, useState } from "react";
import {
  createResourceAllocationOrder,
  deleteResourceAllocationOrder,
  getResourceAllocationOrders,
  getResourceAllocationProjects,
  updateResourceAllocationOrder,
} from "../services/resourceAllocation.service";
import { getResourceAllocationApiView } from "../utils/resourceAllocationView";
import UserContext from "../modules/AppRouter/UserContext";
import type {
  IResourceAllocationOrderApi,
  IResourceAllocationProjectApi,
} from "../services/types/resourceAllocation";

export default function useResourceAllocationOrders(
  employeeId: string | undefined,
  year: number,
  month: number,
  projectId?: string
) {
  const { userData } = useContext(UserContext);
  const workContextRole = userData?.data?.baseRole;

  const [orders, setOrders] = useState<IResourceAllocationOrderApi[]>([]);
  const [isMainSettlementAccepted, setIsMainSettlementAccepted] = useState(false);
  const [projects, setProjects] = useState<IResourceAllocationProjectApi[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const readOnlyMode = getResourceAllocationApiView(workContextRole) === "admin";

  const fetchProjects = useCallback(async () => {
    if (!employeeId) return;
    const data = await getResourceAllocationProjects(employeeId);
    setProjects(data);
  }, [employeeId, workContextRole]);

  const fetchOrders = useCallback(async () => {
    if (!employeeId || month < 1 || month > 12) return;

    setIsLoading(true);
    try {
      const data = await getResourceAllocationOrders(
        employeeId,
        year,
        month,
        projectId || undefined
      );
      setOrders(data.orders);
      setIsMainSettlementAccepted(data.isMainSettlementAccepted);
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, year, month, projectId, workContextRole]);

  const createOrder = async (payload: {
    assignmentDescription: string;
    durationMinutes: number;
    youTrackProjectId: string;
    youTrackProjectLabel: string;
  }) => {
    if (!employeeId || readOnlyMode) return;

    setIsSubmitting(true);
    try {
      await createResourceAllocationOrder(employeeId, {
        year,
        month,
        ...payload,
      });
      await fetchOrders();
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateOrder = async (
    orderId: string,
    payload: {
      youTrackProjectId: string;
      youTrackProjectLabel: string;
      assignmentDescription: string;
      durationMinutes: number;
    }
  ) => {
    if (!employeeId || readOnlyMode) return;

    setIsSubmitting(true);
    try {
      await updateResourceAllocationOrder(employeeId, orderId, payload);
      await fetchOrders();
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteOrder = async (orderId: string) => {
    if (!employeeId || readOnlyMode) return;

    setIsSubmitting(true);
    try {
      await deleteResourceAllocationOrder(employeeId, orderId);
      await fetchOrders();
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return {
    orders,
    projects,
    isLoading,
    isSubmitting,
    readOnlyMode,
    isMainSettlementAccepted,
    refetchOrders: fetchOrders,
    createOrder: readOnlyMode ? undefined : createOrder,
    updateOrder: readOnlyMode ? undefined : updateOrder,
    deleteOrder: readOnlyMode ? undefined : deleteOrder,
  };
}
